import { useCallback, useState } from "react";
import { toast } from "react-hot-toast";
import { useApiServiceCallback } from "../../../server/api/client";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { toISO8601 } from "../../../utils/iso8601";
import { useAnalytics } from "../../analytics/analytics";
import { MomentService } from "../../moment/client";
import { SupabaseMomentService } from "../../moment/server/supabase-moment-service";

import type { UUID } from "../../../utils/uuid";

/**
 * Archives or unarchives a Communication, pausing a finished one first, and says how it went.
 * Resolves to whether it was done.
 */
export function useArchiveMoment(
  id: UUID,
  status: "draft" | "paused" | "finished" | "active" | "archived" | null,
): { archive: (archive: boolean) => Promise<boolean>; archiving: boolean } {
  const [archiving, setArchiving] = useState(false);
  const { gaEvent } = useAnalytics();

  const pauseCallback = useApiServiceCallback(
    MomentService,
    async ({ apiService }): Promise<boolean> => {
      if (status !== "finished") {
        return true;
      }

      const response = await apiService.setActiveStatus(id, false, false);
      if (response.error && response.status !== 400) {
        toast.error(`Something went wrong: ${response.error}`);
        return false;
      }

      gaEvent("pause_moment", { moment_id: id, event_category: "moment" });
      return true;
    },
    [id, status, gaEvent],
  );

  const archiveCallback = useSupabaseCallback(
    async ({ supabase }, archive: boolean): Promise<boolean> => {
      const new_deleted_at = archive ? toISO8601(new Date()) : null;
      const { data, error } = await new SupabaseMomentService(supabase).update(id, {
        deleted_at: new_deleted_at,
      });

      if (!data || error) {
        toast.error(`Something went wrong: ${error}`);
        return false;
      }

      toast.success(
        archive ? "Communication successfully archived" : "Communication successfully unarchived",
      );
      if (archive) {
        gaEvent("archive_moment", { moment_id: id, event_category: "moment" });
      } else {
        gaEvent("unarchive_moment", { moment_id: id, event_category: "moment" });
      }
      return true;
    },
    [id, gaEvent],
  );

  const archive = useCallback(
    async (value: boolean): Promise<boolean> => {
      // Pausing first counts as archiving too.
      setArchiving(true);
      try {
        return (await pauseCallback()) && ((await archiveCallback(value)) ?? false);
      } finally {
        setArchiving(false);
      }
    },
    // oxlint-disable-next-line react/memo-dependencies -- both are used; the rule misses uses inside try
    [archiveCallback, pauseCallback],
  );

  return { archive, archiving };
}
